<template>
    <div class="standard-service-duration-overwrite-per-shift">
        <div class="form-group row">
            <div class="col-lg-3 col-xl-3"></div>
            <div class="col-lg-9 col-xl-6 d-flex justify-content-end">
                <button @click="addOverwrite" type="submit" class="btn btn-success">
                    <i class="la la-plus"></i>{{ i18n("views.OverwriteStandardServiceDurationPerShiftForm.Overwrite") }}
                </button>
            </div>
        </div>
        <div class="form-group row">
            <div class="col-lg-3 col-xl-3"></div>
            <div class="col-lg-9 col-xl-6 d-flex justify-content-end">
                <table class="w-100">
                    <StandardServiceDurationOverwritePerShiftListHeader />
                    <StandardServiceDurationOverwritePerShiftListRow
                        v-for="overwrite in overwrites"
                        :key="overwrite.id"
                        :overwrite="overwrite"
                        @onRemoveOverwrite="onRemoveOverwrite"
                        @onUpdateShift="
                            (shiftId) => {
                                overwrite.shiftId = shiftId;
                            }
                        "
                        :ref="(r) => setRowRef(r, overwrite.id)"
                    />
                </table>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type {
        IStandardServiceDurationOverwriteCreateOrUpdate,
        IStandardServiceDurationOverwriteValidatedDataResult,
    } from "@/data/model/ITableCombination";
    import { i18n } from "@/portal/Trans";
    import { v4 as uuidv4 } from "uuid";
    import { ref } from "vue";

    import StandardServiceDurationOverwritePerShiftListHeader from "@/portal/views/StandardServiceDurationOverwritePerShiftListHeader.vue";
    import StandardServiceDurationOverwritePerShiftListRow from "@/portal/views/StandardServiceDurationOverwritePerShiftListRow.vue";

    const rowRefs = ref<Record<string, typeof StandardServiceDurationOverwritePerShiftListRow>>({});
    const overwrites = ref<IStandardServiceDurationOverwriteCreateOrUpdate[]>([]);

    function addOverwrite(e) {
        e.preventDefault();
        e.stopPropagation();

        overwrites.value.push({
            id: uuidv4(),
            shiftId: undefined,
            standardServiceDuration: undefined,
        });
    }

    function setRowRef(row, id) {
        if (row) {
            rowRefs.value[id] = row;
        } else {
            delete rowRefs.value[id];
        }
    }

    function setData(overwritesToSet: IStandardServiceDurationOverwriteCreateOrUpdate[]) {
        overwrites.value = overwritesToSet;
    }

    function onRemoveOverwrite(overwrite: IStandardServiceDurationOverwriteCreateOrUpdate) {
        overwrites.value = overwrites.value.filter((item) => item.id !== overwrite.id);
    }

    function getValidatedData(): IStandardServiceDurationOverwriteValidatedDataResult {
        if (isDataValid()) {
            return {
                isValid: true,
                overwrites: overwrites.value!.map((r) => {
                    return {
                        id: r.id,
                        shiftId: r.shiftId!,
                        standardServiceDuration: r.standardServiceDuration!,
                    };
                }),
            };
        }

        // data is invalid, return empty array
        return {
            isValid: false,
            overwrites: [],
        };
    }

    function isDataValid(): boolean {
        let hasErrors = false;

        Object.values(rowRefs.value).forEach((r) => {
            if (r.validate() === false) {
                hasErrors = true;
            }
        });

        return !hasErrors;
    }

    defineExpose({
        setData,
        getValidatedData,
    });
</script>

<style scoped lang="scss"></style>
